import { useState } from 'react';

import {
  Account,
  ActionDialogButtons,
  ActionDialogContent,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogTrigger,
  Icon,
  IconButton,
  OffsetPagination,
  Table,
  TableCard,
  TableSkeleton,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@opositandopn/ui';
import classnames from 'classnames';
import {
  IoCheckboxOutline,
  IoClipboardOutline,
  IoReaderOutline,
  IoTrashBinOutline,
} from 'react-icons/io5';

import { ResourceDialog } from '../../../../components/shared/resource-dialog/ResourceDialog';
import { BaseComponentProps } from '../../../../types/base-component.types';
import { formatSortDate } from '../../../../utils/date';
import { MultipleChoiceQuestionView } from '../../../questions/components/multiple-choice-question-view/MultipleChoiceQuestionView';
import {
  ErrorReportViewModel,
  useGetErrorReports,
  useUpdateErrorReport,
} from '../../api/useErrorReports';
import { ErrorReportListFilterForm } from '../error-report-list-filter/ErrorReportListFilter';

import styles from './ErrorReportAdminList.module.scss';

type ContainerProps = BaseComponentProps & {
  filter: ErrorReportListFilterForm;
};

function getTotalItemsBadgeBg(
  totalItems?: number,
): 'featured' | 'success' | 'warning' | 'danger' | 'neutral' {
  if (totalItems === undefined || totalItems === 0) return 'neutral';
  if (totalItems < 10) return 'neutral';
  if (totalItems < 50) return 'warning';
  return 'danger';
}

function ErrorReportAdminList({
  className,
  filter,
  testId = 'ui-error-report-admin-list',
}: ContainerProps) {
  const [current, setCurrent] = useState<ErrorReportViewModel>();
  const [pagination, setPagination] = useState({ pageNumber: 1, pageSize: 20 });

  const { data: response, isLoading } = useGetErrorReports({
    subjectId: filter.subject.id,
    pageNumber: pagination.pageNumber,
    pageSize: pagination.pageSize,
    sort: filter.sort,
    params: {
      fromTime: filter.fromTime,
      toTime: filter.toTime,
      text: filter.text,
      statuses: filter.statuses,
    },
  });

  const { mutate: updateErrorReport } = useUpdateErrorReport();

  return (
    <>
      <TableCard.Root className={className} data-testid={testId}>
        <TableCard.Header
          title="Reportes"
          contentTrailing={
            <Badge bg={getTotalItemsBadgeBg(response?.totalItems)}>
              {response?.totalItems
                ? `Total: ${response?.totalItems}`
                : 'Sin reportes'}
            </Badge>
          }
        />
        <Table aria-label="Usuarios">
          <Table.Header>
            <Table.Head id="username" label="Usuario" isRowHeader />
            <Table.Head id="message" label="Reporte" />
            <Table.Head id="createdAt" label="Fecha de creación" />
            <Table.Head id="actions" />
          </Table.Header>
          {isLoading || response === undefined ? (
            <TableSkeleton columns={4} items={pagination.pageSize} />
          ) : (
            <Table.Body
              items={response.items}
              renderEmptyState={() => 'No hay reportes'}
            >
              {(item: ErrorReportViewModel) => (
                <Table.Row id={item.id}>
                  <Table.Cell>
                    <Account
                      email={item.user.email}
                      username={item.user.username ?? 'Desconocido'}
                      src={
                        item.user.avatarUrl && item.user.avatarUrl.trim() !== ''
                          ? item.user.avatarUrl
                          : undefined
                      }
                      size="sm"
                      allowCopy={
                        !!item.user.email && item.user.email.trim() !== ''
                      }
                    />
                  </Table.Cell>
                  <Table.Cell
                    style={{
                      width: '100%',
                      wordWrap: 'break-word',
                      textWrap: 'pretty',
                      whiteSpace: 'normal',
                    }}
                  >
                    {item.message}
                  </Table.Cell>
                  <Table.Cell>
                    <Text
                      font="monospace"
                      variety="annotation"
                      variant="neutral"
                    >
                      {formatSortDate(new Date(item.createdAt))}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <div className={styles['actions']}>
                      {item.assessments && item.assessments.length > 0 && (
                        <Tooltip placement="top">
                          <TooltipTrigger asChild={true}>
                            <Icon icon={<IoClipboardOutline />} size="sm" />
                          </TooltipTrigger>
                          <TooltipContent className={styles['assessments']}>
                            {item.assessments.map((assessment) => (
                              <Badge key={assessment.id} bg="featured">
                                {assessment.name}
                              </Badge>
                            ))}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      <IconButton
                        size="sm"
                        icon={<IoReaderOutline />}
                        onClick={() => {
                          setCurrent(item);
                        }}
                      />
                      {item.status == 'open' && (
                        <>
                          <Dialog>
                            <DialogTrigger asChild={true}>
                              <IconButton
                                size="sm"
                                icon={<IoCheckboxOutline />}
                                className={classnames(
                                  styles['icon'],
                                  styles['icon--success'],
                                )}
                              />
                            </DialogTrigger>
                            <DialogContent>
                              <ActionDialogContent
                                icon={<IoCheckboxOutline />}
                                title="¿Quieres marcar este reporte como resuelto?"
                              >
                                <ActionDialogButtons>
                                  <Button
                                    variant="outline-primary"
                                    onClick={() => {
                                      updateErrorReport(
                                        {
                                          id: item.id,
                                          subjectId: item.subject.id,
                                          status: 'resolved',
                                        },
                                        {
                                          onSuccess: () => {
                                            document.dispatchEvent(
                                              new KeyboardEvent('keydown', {
                                                key: 'Escape',
                                                keyCode: 27,
                                                which: 27,
                                                code: 'Escape',
                                                bubbles: true,
                                              }),
                                            );
                                          },
                                        },
                                      );
                                    }}
                                  >
                                    Marcar como resuelto
                                  </Button>
                                </ActionDialogButtons>
                              </ActionDialogContent>
                            </DialogContent>
                          </Dialog>
                          <Dialog>
                            <DialogTrigger asChild={true}>
                              <IconButton
                                size="sm"
                                icon={<IoTrashBinOutline />}
                                className={classnames(
                                  styles['icon'],
                                  styles['icon--danger'],
                                )}
                              />
                            </DialogTrigger>
                            <DialogContent>
                              <ActionDialogContent
                                icon={<IoTrashBinOutline />}
                                title="¿Quieres marcar este reporte como falso positivo?"
                              >
                                <ActionDialogButtons>
                                  <Button
                                    variant="outline-danger"
                                    onClick={() => {
                                      updateErrorReport(
                                        {
                                          id: item.id,
                                          subjectId: item.subject.id,
                                          status: 'closed',
                                        },
                                        {
                                          onSuccess: () => {
                                            document.dispatchEvent(
                                              new KeyboardEvent('keydown', {
                                                key: 'Escape',
                                                keyCode: 27,
                                                which: 27,
                                                code: 'Escape',
                                                bubbles: true,
                                              }),
                                            );
                                          },
                                        },
                                      );
                                    }}
                                  >
                                    Cerrar reporte
                                  </Button>
                                </ActionDialogButtons>
                              </ActionDialogContent>
                            </DialogContent>
                          </Dialog>
                        </>
                      )}
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          )}
        </Table>
        {response && (
          <OffsetPagination
            className={styles.pagination}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalRecords={response.totalItems}
            onPageChange={(pageNumber: number) => {
              setPagination({ ...pagination, pageNumber });
            }}
            pageNeighbours={2}
          />
        )}
      </TableCard.Root>
      <ResourceDialog open={current !== undefined}>
        {current && current.format === 'multiple_choice' && (
          <MultipleChoiceQuestionView
            subjectId={filter.subject.id}
            questionId={current.questionId}
            onClose={() => {
              setCurrent(undefined);
            }}
          />
        )}
      </ResourceDialog>
    </>
  );
}

export { ErrorReportAdminList };
